import { ld } from "@/lib/theme/light-dark";
import type { CSSProperties, ReactNode } from "react";

/**
 * The backdrop behind framed elements: a fine dot grid (1px dots on a 22px
 * raster) that fades out radially, so the framed element seems to sit on graph
 * paper. `tone` picks ink dots on white (`light`) or white dots on the dark CTA
 * panel (`dark`); `focus` puts the visible area behind the centre (product
 * window) or under the top edge (spotlight for the CTA panel).
 *
 * Absolutely positioned — the parent must be `position: relative`. Pass `style`
 * to bleed past the parent (negative `top` / `bottom`).
 */
export function DotGridBackdrop(props: {
  readonly tone: "light" | "dark";
  readonly focus: "center" | "top";
  readonly style?: CSSProperties;
}): ReactNode {
  const dot: string =
    props.tone === "dark" ? "rgba(255,255,255,0.16)" : ld("rgba(11,15,25,0.16)", "rgba(250,250,250,0.16)");
  const mask: string =
    props.focus === "top"
      ? "radial-gradient(70% 70% at 50% 0%, #000 0%, rgba(0,0,0,0.5) 45%, transparent 100%)"
      : "radial-gradient(ellipse 72% 68% at 50% 50%, #000 35%, transparent 100%)";

  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        backgroundImage: `radial-gradient(${dot} 1px, transparent 1.4px)`,
        backgroundSize: "22px 22px",
        backgroundPosition: "center center",
        WebkitMaskImage: mask,
        maskImage: mask,
        ...props.style,
      }}
    />
  );
}
